"use client";

import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import type { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import type { SupportedChain } from "@/components/nft-picker/NftPicker.types";
import { VirtualizedTokenList } from "@/components/token-list/VirtualizedTokenList";
import Spinner from "@/components/utils/Spinner";
import type { GrantTokensDisclosureState } from "../types";
import { useGrantTokensDisclosure } from "../hooks/useGrantTokensDisclosure";
const END_REACHED_OFFSET = 48;
interface GrantTokensDisclosureProps {
  readonly chain: SupportedChain | null;
  readonly contractAddress: `0x${string}` | null;
  readonly grantId: string;
  readonly tokensCount: number | null;
  readonly tokensCountLabel: string;
}
export function GrantTokensDisclosure(t0) {
  const $ = _c(28);
  const {
    chain,
    contractAddress,
    grantId,
    tokensCount,
    tokensCountLabel
  } = t0;
  let t1;
  if ($[0] !== chain || $[1] !== contractAddress || $[2] !== grantId) {
    t1 = {
      chain,
      contractAddress,
      grantId
    };
    $[0] = chain;
    $[1] = contractAddress;
    $[2] = grantId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    isOpen,
    panelId,
    toggleOpen,
    disclosureState
  } = useGrantTokensDisclosure(t1);
  const tokensCountWord = tokensCount === 1 ? "token" : "tokens";
  const tokensCountDescription = tokensCount == null ? tokensCountLabel : `${tokensCountLabel} ${tokensCountWord}`;
  let t2;
  if ($[4] !== disclosureState) {
    t2 = renderGrantTokensDisclosureBody(disclosureState);
    $[4] = disclosureState;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const body = t2;
  const t3 = isOpen ? "tw-bg-iron-900" : "tw-bg-iron-900/30";
  let t4;
  if ($[6] !== t3) {
    t4 = clsx("tw-group tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-none tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-appearance-none focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-bg-iron-900", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isOpen ? `Hide ${tokensCountDescription}` : `Show ${tokensCountDescription}`;
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="tw-text-sm tw-font-semibold tw-text-iron-400 tw-transition-colors tw-duration-200 desktop-hover:group-hover:tw-text-iron-200">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isOpen && "tw-rotate-180";
  let t8;
  if ($[10] !== t7) {
    t8 = clsx("tw-size-4 tw-text-iron-400 tw-transition-all tw-duration-200 desktop-hover:group-hover:tw-text-iron-200", t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" className={t8} />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== isOpen || $[15] !== panelId || $[16] !== t4 || $[17] !== t6 || $[18] !== t9 || $[19] !== toggleOpen) {
    t10 = <button type="button" className={t4} onClick={toggleOpen} aria-expanded={isOpen} aria-controls={panelId}>{t6}{t9}</button>;
    $[14] = isOpen;
    $[15] = panelId;
    $[16] = t4;
    $[17] = t6;
    $[18] = t9;
    $[19] = toggleOpen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== body || $[22] !== isOpen || $[23] !== panelId) {
    t11 = isOpen ? <div id={panelId} className="tw-border-t tw-border-iron-800 tw-bg-iron-950 tw-p-3">{body}</div> : null;
    $[21] = body;
    $[22] = isOpen;
    $[23] = panelId;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-solid tw-border-iron-900 tw-bg-iron-950">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function renderGrantTokensDisclosureBody({
  showInitialLoading,
  showInitialError,
  tokenRanges,
  tokens,
  errorMessage,
  onRetry,
  contractAddress,
  chain,
  grantId,
  onEndReached,
  isFetchingNextPage
}: GrantTokensDisclosureState): ReactNode {
  if (showInitialLoading) {
    return <GrantTokensLoadingState />;
  }
  if (showInitialError) {
    return <GrantTokensErrorState message={errorMessage} onRetry={onRetry} />;
  }
  if (tokenRanges.length === 0) {
    return <GrantTokensEmptyState />;
  }
  const mappedTokens = tokens.map(t => ({
    tokenId: BigInt(t.token),
    xtdh: t.xtdh
  }));
  return <>
      <VirtualizedTokenList contractAddress={contractAddress ?? undefined} chain={chain} ranges={tokenRanges} scrollKey={`grant-token-list-${grantId}`} className="" scrollContainerClassName="tw-max-h-[480px] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300" onEndReached={onEndReached} endReachedOffset={END_REACHED_OFFSET} layout="grid" tokens={mappedTokens} />
      {isFetchingNextPage ? <GrantTokensLoadingMore /> : null}
    </>;
}
function GrantTokensLoadingState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-min-h-24 tw-items-center tw-justify-center"><Spinner /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function GrantTokensErrorState(t0) {
  const $ = _c(7);
  const {
    message,
    onRetry
  } = t0;
  let t1;
  if ($[0] !== message) {
    t1 = <p className="tw-m-0 tw-text-sm tw-text-red-300">{message}</p>;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onRetry) {
    t2 = <button type="button" className="tw-mt-3 tw-rounded-md tw-border tw-border-red-500/60 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-red-200 desktop-hover:hover:tw-bg-red-500/10" onClick={onRetry}>Retry</button>;
    $[2] = onRetry;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-rounded-lg tw-border tw-border-red-500/40 tw-bg-red-500/5 tw-p-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function GrantTokensEmptyState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-rounded-lg tw-border tw-border-iron-800 tw-bg-iron-925 tw-p-4"><p className="tw-m-0 tw-text-sm tw-text-iron-300">No specific token IDs were returned for this grant.</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function GrantTokensLoadingMore() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-items-center tw-justify-center tw-border-t tw-border-iron-800 tw-bg-iron-900 tw-py-2 tw-text-xs tw-text-iron-300"><Spinner /><span className="tw-ml-2">Loading more tokens…</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}